import { TextValidatorType } from "anketa-core/base";
import { Group, Section } from "anketa-core/group";
import { Translation } from "anketa-core/i18n";
import { OpenQuestion } from "anketa-core/open_question";
import { OptionsQuestion } from "anketa-core/options_question";
import { TextValidator } from "anketa-core/validation";

export const setupProxmoxGroup = (proxmoxScanners: Section, idx = 0) => {
  const proxmoxGroup = proxmoxScanner(idx);
  proxmoxScanners.addChild(proxmoxGroup.proxmoxScanner);

  function onConfigChange() {
    const name = proxmoxGroup.proxmoxName.fact.raw;

    if (name && name.toString().trim() !== "") {
      proxmoxGroup.proxmoxName.removeChangeListener(onConfigChange);
      setupProxmoxGroup(proxmoxScanners, idx + 1);
    }
  }

  proxmoxGroup.proxmoxName.addChangeListener(onConfigChange);
};

export const proxmoxScanner = (
  idx: number
): { proxmoxScanner: Group; proxmoxName: OpenQuestion } => {
  const idPrefix = `proxmox_scanner_${idx}`;

  const proxmoxScanner = new Group(idPrefix, [
    new Translation("en", "Unconfigured Proxmox Scanner"),
    new Translation("de", "Unkonfigurierter Proxmox Scanner"),
  ]);

  const name = new OpenQuestion(`${idPrefix}_name`, [
    new Translation("en", "Display name"),
    new Translation("de", "Anzeigename"),
  ]);
  name.validator = new TextValidator();
  name.required = true;
  name.addChangeListener(() => {
    const plainName = name.fact.raw.toString();
    if (plainName.trim() !== "") {
      proxmoxScanner.title = [
        new Translation("en", plainName),
        new Translation("de", plainName),
      ];
    } else {
      proxmoxScanner.title = [
        new Translation("en", "Unconfigured Proxmox Scanner"),
        new Translation("de", "Unkonfigurierter Proxmox Scanner"),
      ];
    }
  });
  proxmoxScanner.addChild(name);

  const proxmoxUrl = new OpenQuestion(`${idPrefix}_url`, [
    new Translation("en", "Proxmox API URL"),
    new Translation("de", "Proxmox API-URL"),
  ]);
  proxmoxUrl.validator = new TextValidator(TextValidatorType.Uri);
  proxmoxUrl.required = true;
  proxmoxUrl.help = [
    new Translation(
      "en",
      "The full URL to your Proxmox VE API (e.g., https://proxmox.example.com:8006/api2/json)."
    ),
    new Translation(
      "de",
      "Die vollständige URL zu Ihrer Proxmox VE API (z.B. https://proxmox.beispiel.de:8006/api2/json)."
    ),
  ];
  proxmoxScanner.addChild(proxmoxUrl);

  const proxmoxApiToken = new OpenQuestion(`${idPrefix}_api_token`, [
    new Translation("en", "API Token"),
    new Translation("de", "API-Token"),
  ]);
  proxmoxApiToken.required = true;
  proxmoxApiToken.readonly = true;
  proxmoxApiToken.help = [
    new Translation(
      "en",
      "A Proxmox API token with read-only access to devices. The format is username@realm!tokenid=tokensecret."
    ),
    new Translation(
      "de",
      "Ein Proxmox API-Token mit Nur-Lesen-Zugriff auf Geräte. Das Format ist benutzername@realm!tokenid=tokensecret."
    ),
  ];
  proxmoxScanner.addChild(proxmoxApiToken);

  const proxmoxInsecure = new OptionsQuestion(
    `${idPrefix}_insecure`,
    [
      new Translation("en", "Disable Certificate Verification"),
      new Translation("de", "Zertifikatsüberprüfung deaktivieren"),
    ],
    "default.options.yes_no"
  );
  proxmoxInsecure.help = [
    new Translation(
      "en",
      "Should SSL/TLS certificate verification be disabled? Only use this for testing with self-signed certificates."
    ),
    new Translation(
      "de",
      "Soll die Überprüfung von SSL/TLS-Zertifikaten deaktiviert werden? Nur für Tests mit selbstsignierten Zertifikaten verwenden."
    ),
  ];
  if (proxmoxInsecure.fact.raw === null) {
    proxmoxInsecure.setFactValue(1);
  }
  proxmoxScanner.addChild(proxmoxInsecure);

  const proxmoxDistrustOSData = new OptionsQuestion(
    `${idPrefix}_distrust_os_data`,
    [
      new Translation("en", "Distrust Guest OS Data"),
      new Translation("de", "Gast-OS-Daten nicht vertrauen"),
    ],
    "default.options.yes_no"
  );
  proxmoxDistrustOSData.help = [
    new Translation(
      "en",
      "If enabled, device data reported by the guest OS will be ignored in favor of hypervisor data."
    ),
    new Translation(
      "de",
      "Wenn aktiviert, werden vom Gastbetriebssystem gemeldete Gerätedaten zugunsten der Hypervisor-Daten ignoriert."
    ),
  ];
  if (proxmoxDistrustOSData.fact.raw === null) {
    proxmoxDistrustOSData.setFactValue(0);
  }
  proxmoxScanner.addChild(proxmoxDistrustOSData);

  return { proxmoxScanner, proxmoxName: name };
};
